// src/admin/AdminBulkLog.jsx
import React, { useState, useEffect } from 'react';
import { supabase } from '../services/supabase';
import {
  FiPlus, FiEdit, FiTrash2, FiSearch, FiFilter,
  FiDollarSign, FiPackage, FiRefreshCw, FiX, FiSave,
  FiEye, FiCheckCircle, FiClock, FiXCircle, FiCopy,
  FiUser, FiMail, FiPhone, FiMapPin, FiCalendar,
  FiMessageSquare, FiSend, FiDownload, FiCheck,
  FiFolder, FiTag, FiTarget, FiAlertCircle, FiList,
  FiUsers, FiSettings, FiPrinter, FiRefreshCcw
} from 'react-icons/fi';
import styles from './AdminBulkLog.module.css';

const AdminBulkLog = () => {
  // ── State ──
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);
  const [admins, setAdmins] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [profitMarginTarget, setProfitMarginTarget] = useState(20);

  const [selectedOrders, setSelectedOrders] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    customer_name: '',
    phone: '', // NEW
    order_date: new Date().toISOString().slice(0, 10),
    status: 'pending',
    notes: '',
    source: 'WhatsApp',
    assigned_to: '',
    gst_rate: 0,
    discount_percent: 0,
    discount_amount: 0,
    profit_margin_target: 20,
  });
  const [items, setItems] = useState([]);
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState('');

  const [orderNotes, setOrderNotes] = useState([]);
  const [noteText, setNoteText] = useState('');

  const [showCustomerProfile, setShowCustomerProfile] = useState(false);
  const [profileCustomer, setProfileCustomer] = useState(null);
  const [customerProfile, setCustomerProfile] = useState(null);
  const [customerOrders, setCustomerOrders] = useState([]);

  const [showStatusManager, setShowStatusManager] = useState(false);
  const [newStatusName, setNewStatusName] = useState('');
  const [newStatusColor, setNewStatusColor] = useState('#6B7280');

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  // ── Fetch data ──
  useEffect(() => {
    fetchOrders();
    fetchAdmins();
    fetchStatuses();
    const channel = supabase
      .channel('bulk-log-changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'bulk_order_logs' }, () => fetchOrders())
      .on('postgres_changes', { event: '*', schema: 'public', table: 'bulk_order_log_items' }, () => fetchOrders())
      .on('postgres_changes', { event: '*', schema: 'public', table: 'bulk_order_notes' }, () => fetchOrders())
      .subscribe();
    return () => supabase.removeChannel(channel);
  }, []);

  const fetchOrders = async () => {
    try {
      const { data, error } = await supabase
        .from('bulk_order_logs')
        .select('*, bulk_order_log_items(*)')
        .order('created_at', { ascending: false });
      if (error) throw error;
      setOrders(data || []);
    } catch (err) {
      console.error('Fetch error:', err);
      setError(err.message);
      showToast('Failed to fetch orders: ' + err.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchAdmins = async () => {
    const { data } = await supabase.from('users').select('id, name').eq('is_admin', true);
    setAdmins(data || []);
  };

  const fetchStatuses = async () => {
    const { data } = await supabase.from('bulk_order_statuses').select('*').order('name');
    setStatuses(data || []);
  };

  // ── Delete order ──
  const deleteOrder = async (orderId) => {
    if (!window.confirm('Delete this order and all items?')) return;
    try {
      const { error } = await supabase.from('bulk_order_logs').delete().eq('id', orderId);
      if (error) throw error;
      showToast('Order deleted successfully', 'success');
      await fetchOrders();
    } catch (err) {
      alert('Delete failed: ' + err.message);
    }
  };

  // ── Open modal ──
  const openModal = (order = null) => {
    if (order) {
      setEditingId(order.id);
      setFormData({
        customer_name: order.customer_name || '',
        phone: order.phone || '', // NEW
        order_date: order.order_date ? order.order_date.slice(0, 10) : new Date().toISOString().slice(0, 10),
        status: order.status || 'pending',
        notes: order.notes || '',
        source: order.source || 'WhatsApp',
        assigned_to: order.assigned_to || '',
        gst_rate: order.gst_rate || 0,
        discount_percent: order.discount_percent || 0,
        discount_amount: order.discount_amount || 0,
        profit_margin_target: order.profit_margin_target || 20,
      });
      setItems(order.bulk_order_log_items || []);
      setOrderNotes([]);
      fetchOrderNotes(order.id);
    } else {
      setEditingId(null);
      setFormData({
        customer_name: '',
        phone: '', // NEW
        order_date: new Date().toISOString().slice(0, 10),
        status: 'pending',
        notes: '',
        source: 'WhatsApp',
        assigned_to: '',
        gst_rate: 0,
        discount_percent: 0,
        discount_amount: 0,
        profit_margin_target: 20,
      });
      setItems([]);
      setOrderNotes([]);
    }
    setFormError('');
    setShowModal(true);
  };

  const fetchOrderNotes = async (orderId) => {
    const { data } = await supabase
      .from('bulk_order_notes')
      .select('*, users(name)')
      .eq('order_id', orderId)
      .order('created_at', { ascending: false });
    setOrderNotes(data || []);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingId(null);
    setItems([]);
    setOrderNotes([]);
    setFormError('');
    setFormLoading(false);
  };

  // ── Form handlers ──
  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...items];
    newItems[index][field] = value;
    if (field === 'cost_price' && parseFloat(value) > 0 && !newItems[index].selling_price) {
      const target = formData.profit_margin_target || profitMarginTarget;
      const suggested = parseFloat(value) * (1 + target / 100);
      newItems[index].selling_price = Math.round(suggested * 100) / 100;
    }
    setItems(newItems);
  };

  const addItemRow = () => {
    setItems([...items, { item_name: '', quantity: 1, selling_price: 0, cost_price: 0 }]);
  };

  const removeItemRow = (index) => {
    if (items.length <= 1) return;
    setItems(items.filter((_, i) => i !== index));
  };

  // ── Save order ──
  const handleSave = async () => {
    if (!formData.customer_name.trim()) {
      setFormError('Customer name is required');
      return;
    }
    if (items.length === 0) {
      setFormError('Add at least one item');
      return;
    }
    if (items.some(item => !item.item_name.trim())) {
      setFormError('All items must have a name');
      return;
    }

    setFormLoading(true);
    setFormError('');

    try {
      const totalSelling = items.reduce((sum, i) => sum + (parseFloat(i.selling_price) || 0) * (parseInt(i.quantity) || 0), 0);
      const totalCost = items.reduce((sum, i) => sum + (parseFloat(i.cost_price) || 0) * (parseInt(i.quantity) || 0), 0);
      const totalProfit = totalSelling - totalCost;
      const taxAmount = totalSelling * (parseFloat(formData.gst_rate) || 0) / 100;
      const discountAmount = formData.discount_amount || (totalSelling * (parseFloat(formData.discount_percent) || 0) / 100);
      const finalTotal = totalSelling + taxAmount - discountAmount;

      const orderPayload = {
        customer_name: formData.customer_name,
        phone: formData.phone || null, // NEW
        order_date: formData.order_date,
        status: formData.status,
        notes: formData.notes,
        source: formData.source,
        assigned_to: formData.assigned_to || null,
        gst_rate: parseFloat(formData.gst_rate) || 0,
        discount_percent: parseFloat(formData.discount_percent) || 0,
        discount_amount: discountAmount,
        tax_amount: taxAmount,
        profit_margin_target: parseFloat(formData.profit_margin_target) || 20,
        total_selling: totalSelling,
        total_cost: totalCost,
        total_profit: totalProfit,
      };

      let orderId;
      if (editingId) {
        const { error } = await supabase
          .from('bulk_order_logs')
          .update(orderPayload)
          .eq('id', editingId);
        if (error) throw error;
        orderId = editingId;
        await supabase.from('bulk_order_log_items').delete().eq('bulk_order_log_id', orderId);
      } else {
        const { data: newOrder, error } = await supabase
          .from('bulk_order_logs')
          .insert(orderPayload)
          .select()
          .single();
        if (error) throw error;
        orderId = newOrder.id;
        await updateCustomerProfile(formData.customer_name, finalTotal);
      }

      if (items.length > 0) {
        const itemsPayload = items.map(item => ({
          bulk_order_log_id: orderId,
          item_name: item.item_name,
          quantity: parseInt(item.quantity) || 1,
          selling_price: parseFloat(item.selling_price) || 0,
          cost_price: parseFloat(item.cost_price) || 0,
        }));
        const { error: itemsError } = await supabase
          .from('bulk_order_log_items')
          .insert(itemsPayload);
        if (itemsError) throw itemsError;
      }

      showToast(`Order ${editingId ? 'updated' : 'created'} successfully!`, 'success');
      closeModal();
      await fetchOrders();
    } catch (err) {
      setFormError(err.message);
      showToast('Error saving order: ' + err.message, 'error');
    } finally {
      setFormLoading(false);
    }
  };

  // ── Customer profile update ──
  const updateCustomerProfile = async (name, amount) => {
    const { data: existing } = await supabase
      .from('bulk_customer_profiles')
      .select('*')
      .ilike('customer_name', name)
      .maybeSingle();
    if (existing) {
      await supabase
        .from('bulk_customer_profiles')
        .update({
          total_orders: existing.total_orders + 1,
          total_spent: existing.total_spent + amount,
          last_order_date: new Date().toISOString(),
        })
        .eq('id', existing.id);
    } else {
      await supabase
        .from('bulk_customer_profiles')
        .insert({
          customer_name: name,
          total_orders: 1,
          total_spent: amount,
          last_order_date: new Date().toISOString(),
        });
    }
  };

  // ── Sync Customer Profiles ──
  const syncCustomerProfiles = async () => {
    const { data: allOrders } = await supabase
      .from('bulk_order_logs')
      .select('customer_name, total_selling, created_at');
    const names = [...new Set(allOrders.map(o => o.customer_name).filter(Boolean))];
    let count = 0;
    for (const name of names) {
      const { data: custOrders } = await supabase
        .from('bulk_order_logs')
        .select('total_selling, created_at')
        .ilike('customer_name', name);
      if (custOrders && custOrders.length > 0) {
        const totalSpent = custOrders.reduce((sum, o) => sum + (o.total_selling || 0), 0);
        const lastOrder = custOrders.reduce((latest, o) => o.created_at > latest ? o.created_at : latest, '');
        const { error } = await supabase
          .from('bulk_customer_profiles')
          .upsert({
            customer_name: name,
            total_orders: custOrders.length,
            total_spent: totalSpent,
            last_order_date: lastOrder,
          }, { onConflict: 'customer_name' });
        if (!error) count++;
      }
    }
    showToast(`Synced ${count} customer profiles`, 'success');
  };

  // ── Add note ──
  const addNote = async () => {
    if (!noteText.trim()) return;
    const { data: { user } } = await supabase.auth.getUser();
    await supabase.from('bulk_order_notes').insert({
      order_id: editingId,
      note: noteText,
      created_by: user?.id,
    });
    setNoteText('');
    fetchOrderNotes(editingId);
  };

  // ── Fulfillment toggle ──
  const toggleFulfillment = async (orderId, step) => {
    const order = orders.find(o => o.id === orderId);
    if (!order) return;
    const newFulfillment = { ...order.fulfillment, [step]: !order.fulfillment[step] };
    await supabase
      .from('bulk_order_logs')
      .update({ fulfillment: newFulfillment })
      .eq('id', orderId);
    if (Object.values(newFulfillment).every(v => v === true)) {
      await supabase
        .from('bulk_order_logs')
        .update({ status: 'completed' })
        .eq('id', orderId);
      showToast('Order completed!', 'success');
    }
    fetchOrders();
  };

  // ── Reorder ──
  const duplicateOrder = async (orderId) => {
    const order = orders.find(o => o.id === orderId);
    if (!order) return;
    const newOrder = {
      customer_name: order.customer_name,
      phone: order.phone, // NEW
      notes: `Reordered from #${order.order_number || orderId}`,
      status: 'pending',
      source: order.source,
      assigned_to: order.assigned_to,
      gst_rate: order.gst_rate,
      discount_percent: order.discount_percent,
      profit_margin_target: order.profit_margin_target,
    };
    const { data: created } = await supabase
      .from('bulk_order_logs')
      .insert(newOrder)
      .select()
      .single();
    if (created) {
      const itemsCopy = order.bulk_order_log_items.map(item => ({
        bulk_order_log_id: created.id,
        item_name: item.item_name,
        quantity: item.quantity,
        selling_price: item.selling_price,
        cost_price: item.cost_price,
      }));
      await supabase.from('bulk_order_log_items').insert(itemsCopy);
      showToast('Order duplicated successfully!', 'success');
      fetchOrders();
    }
  };

  // ── Generate full invoice HTML ──
  const generateInvoiceHTML = (order) => {
    const items = order.bulk_order_log_items || [];
    const totalSelling = items.reduce((s, i) => s + (i.selling_price || 0) * (i.quantity || 0), 0);
    const taxAmount = totalSelling * ((order.gst_rate || 0) / 100);
    const discount = (totalSelling * ((order.discount_percent || 0) / 100)) + (order.discount_amount || 0);
    const grandTotal = totalSelling + taxAmount - discount;

    return `
      <div class="invoice-page">
        <div class="invoice-header">
          <h1>ETIKOPPAKA TOYS</h1>
          <p>Traditional Handcrafted Wooden Toys | Since 1985</p>
          <p>📍 Etikoppaka, Visakhapatnam, AP - 531082 | 📞 +91 9154884214</p>
          <h2 style="color: #1F5B3A;">TAX INVOICE</h2>
        </div>

        <div class="invoice-details">
          <div><span class="label">Order #:</span> ${order.order_number || 'N/A'}</div>
          <div><span class="label">Date:</span> ${new Date(order.order_date || order.created_at).toLocaleDateString('en-IN')}</div>
          <div><span class="label">Customer:</span> ${order.customer_name}</div>
          <div><span class="label">Phone:</span> ${order.phone || 'N/A'}</div>
          <div><span class="label">Source:</span> ${order.source || 'Other'}</div>
          <div><span class="label">Status:</span> ${order.status || 'Pending'}</div>
        </div>

        <table class="invoice-table">
          <thead><tr><th>Item</th><th>Qty</th><th>Price</th><th>Total</th></tr></thead>
          <tbody>
            ${items.map(item => `
              <tr>
                <td>${item.item_name}</td>
                <td>x${item.quantity}</td>
                <td>₹${(item.selling_price || 0).toFixed(2)}</td>
                <td>₹${((item.selling_price || 0) * (item.quantity || 0)).toFixed(2)}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>

        <div class="invoice-totals">
          <div class="total-row"><span>Subtotal:</span><span>₹${totalSelling.toFixed(2)}</span></div>
          ${order.gst_rate ? `<div class="total-row"><span>GST (${order.gst_rate}%):</span><span>₹${taxAmount.toFixed(2)}</span></div>` : ''}
          ${discount ? `<div class="total-row"><span>Discount:</span><span>-₹${discount.toFixed(2)}</span></div>` : ''}
          <div class="total-row grand-total"><span>Grand Total:</span><span>₹${grandTotal.toFixed(2)}</span></div>
        </div>

        <div class="footer">
          ✨ Each piece is handcrafted with natural colors & sustainable wood ✨<br>
          © ${new Date().getFullYear()} Etikoppaka Toys. Thank you for supporting traditional craftsmanship!
        </div>
      </div>
    `;
  };

  // ── Batch Invoice ──
  const generateBatchInvoice = () => {
    const ordersToPrint = orders.filter(o => selectedOrders.includes(o.id));
    if (ordersToPrint.length === 0) {
      showToast('Select at least one order', 'error');
      return;
    }

    const allHtml = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>Batch Invoices</title>
        <style>
          @page { margin: 0; }
          body { 
            font-family: 'Georgia', 'Times New Roman', serif;
            background: #F8F2E8;
            padding: 20px;
            margin: 0;
          }
          .invoice-page {
            page-break-after: always;
            background: white;
            max-width: 800px;
            margin: 0 auto 30px;
            padding: 40px;
            border-radius: 16px;
            box-shadow: 0 4px 20px rgba(0,0,0,0.08);
          }
          .invoice-header {
            text-align: center;
            border-bottom: 2px solid #1F5B3A;
            padding-bottom: 20px;
            margin-bottom: 20px;
          }
          .invoice-header h1 { font-size: 24px; color: #1F5B3A; margin: 0; }
          .invoice-header p { color: #7A6B5A; margin: 5px 0; }
          .invoice-details {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 10px;
            background: #F8F2E8;
            padding: 15px;
            border-radius: 10px;
            margin-bottom: 20px;
          }
          .invoice-details .label { font-weight: 600; color: #3E2A1F; }
          .invoice-table { width: 100%; border-collapse: collapse; margin: 15px 0; }
          .invoice-table th {
            background: #1F5B3A;
            color: white;
            padding: 8px;
            text-align: left;
          }
          .invoice-table td { padding: 8px; border-bottom: 1px solid #E8DCC8; }
          .invoice-totals {
            text-align: right;
            margin-top: 15px;
            padding-top: 10px;
            border-top: 2px solid #1F5B3A;
          }
          .invoice-totals .total-row {
            display: flex;
            justify-content: flex-end;
            gap: 30px;
            padding: 4px 0;
          }
          .grand-total { font-size: 18px; font-weight: bold; color: #9E1B1B; }
          .footer {
            text-align: center;
            margin-top: 30px;
            padding-top: 15px;
            border-top: 1px dashed #C89B3C;
            color: #7A6B5A;
            font-size: 10px;
          }
          @media print {
            body { background: white; padding: 0; }
            .invoice-page { box-shadow: none; margin: 0 auto; border-radius: 0; }
          }
        </style>
      </head>
      <body>
        ${ordersToPrint.map(order => generateInvoiceHTML(order)).join('')}
      </body>
      </html>
    `;

    const printWindow = window.open('', '_blank', 'width=800,height=600');
    if (printWindow) {
      printWindow.document.write(allHtml);
      printWindow.document.close();
      printWindow.print();
    }
  };

  // ── Status manager ──
  const addCustomStatus = async () => {
    if (!newStatusName.trim()) return;
    const { error } = await supabase
      .from('bulk_order_statuses')
      .insert({ name: newStatusName, color: newStatusColor });
    if (!error) {
      showToast('Status added', 'success');
      fetchStatuses();
      setNewStatusName('');
    }
  };

  const deleteStatus = async (id) => {
    await supabase.from('bulk_order_statuses').delete().eq('id', id);
    fetchStatuses();
  };

  // ── Customer profile (fixed with phone) ──
  const openCustomerProfile = async (name) => {
    setProfileCustomer(name);
    // Fetch orders for this customer (case‑insensitive)
    const { data: orders } = await supabase
      .from('bulk_order_logs')
      .select('*, bulk_order_log_items(*)')
      .ilike('customer_name', name)
      .order('created_at', { ascending: false });
    setCustomerOrders(orders || []);

    // Calculate totals
    const totalOrders = orders?.length || 0;
    const totalSpent = orders?.reduce((sum, o) => sum + (o.total_selling || 0), 0) || 0;
    const lastOrder = orders?.length > 0 ? orders[0].created_at : null;
    // Get phone from most recent order with a phone
    let phone = '';
    if (orders && orders.length > 0) {
      const orderWithPhone = orders.find(o => o.phone);
      if (orderWithPhone) phone = orderWithPhone.phone;
    }

    // Try to fetch existing profile
    let { data: profile } = await supabase
      .from('bulk_customer_profiles')
      .select('*')
      .ilike('customer_name', name)
      .maybeSingle();

    if (profile) {
      // Update profile with latest data if needed
      if (profile.total_orders !== totalOrders || profile.total_spent !== totalSpent || profile.phone !== phone) {
        await supabase
          .from('bulk_customer_profiles')
          .update({
            total_orders: totalOrders,
            total_spent: totalSpent,
            last_order_date: lastOrder || profile.last_order_date,
            phone: phone || profile.phone,
          })
          .eq('id', profile.id);
        // Re‑fetch updated profile
        const { data: updated } = await supabase
          .from('bulk_customer_profiles')
          .select('*')
          .eq('id', profile.id)
          .single();
        profile = updated;
      }
    } else {
      // No profile – create one with calculated data
      if (totalOrders > 0) {
        const { data: newProfile } = await supabase
          .from('bulk_customer_profiles')
          .insert({
            customer_name: name,
            total_orders: totalOrders,
            total_spent: totalSpent,
            last_order_date: lastOrder,
            phone: phone,
          })
          .select()
          .single();
        profile = newProfile;
      } else {
        // Placeholder
        profile = {
          customer_name: name,
          total_orders: 0,
          total_spent: 0,
          last_order_date: null,
          email: '',
          phone: phone || '',
          notes: ''
        };
      }
    }

    setCustomerProfile(profile);
    setShowCustomerProfile(true);
  };

  // ── Helpers ──
  const getStatusColor = (status) => {
    const found = statuses.find(s => s.name === status);
    return found ? found.color : '#6B7280';
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Pending': return <FiClock size={12} />;
      case 'Processing': return <FiClock size={12} />;
      case 'Quoted': return <FiEye size={12} />;
      case 'Approved': return <FiCheckCircle size={12} />;
      case 'Completed': return <FiCheckCircle size={12} />;
      case 'Cancelled': return <FiXCircle size={12} />;
      default: return <FiTag size={12} />;
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(value || 0);
  };

  const totalOrders = orders.length;
  const totalProfit = orders.reduce((sum, o) => sum + (o.total_profit || 0), 0);
  const totalSelling = orders.reduce((sum, o) => sum + (o.total_selling || 0), 0);

  const filteredOrders = orders.filter(order => {
    const matchSearch = (order.customer_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                        (order.phone || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                        (order.notes || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                        (order.source || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchStatus = statusFilter === 'all' || order.status === statusFilter;
    return matchSearch && matchStatus;
  });

  const toggleSelection = (id) => {
    if (selectedOrders.includes(id)) {
      setSelectedOrders(selectedOrders.filter(o => o !== id));
    } else {
      setSelectedOrders([...selectedOrders, id]);
    }
  };

  const toggleSelectAll = () => {
    if (selectAll) {
      setSelectedOrders([]);
    } else {
      setSelectedOrders(filteredOrders.map(o => o.id));
    }
    setSelectAll(!selectAll);
  };

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.loadingSpinner}></div>
        <p>Loading bulk orders...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.errorContainer}>
        <div className={styles.errorIcon}>⚠️</div>
        <h3>Error Loading Data</h3>
        <p>{error}</p>
        <button onClick={fetchOrders} className={styles.retryBtn}><FiRefreshCw /> Retry</button>
      </div>
    );
  }

  return (
    <div className={styles.adminBulkLog}>
      {toast && (
        <div className={`${styles.toast} ${styles[toast.type]}`}>
          {toast.message}
        </div>
      )}

      {/* ── Header ── */}
      <div className={styles.header}>
        <div>
          <h2>Bulk Order Log</h2>
          <p>Log and manage orders from WhatsApp, Instagram, etc.</p>
        </div>
        <div className={styles.headerActions}>
          <button onClick={fetchOrders} className={styles.refreshBtn} disabled={loading}>
            <FiRefreshCw /> Refresh
          </button>
          <button onClick={syncCustomerProfiles} className={styles.syncBtn}>
            <FiRefreshCcw /> Sync Profiles
          </button>
          <button onClick={() => setShowStatusManager(true)} className={styles.statusManagerBtn}>
            <FiSettings /> Statuses
          </button>
          <button onClick={() => openModal()} className={styles.addBtn}>
            <FiPlus /> New Order
          </button>
        </div>
      </div>

      {/* ── Stats Cards ── */}
      <div className={styles.statsGrid}>
        <div className={styles.statCard}>
          <div className={styles.statIcon}><FiPackage /></div>
          <div className={styles.statInfo}>
            <span className={styles.statValue}>{totalOrders}</span>
            <span className={styles.statLabel}>Total Orders</span>
          </div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statIcon}><FiDollarSign /></div>
          <div className={styles.statInfo}>
            <span className={styles.statValue}>{formatCurrency(totalSelling)}</span>
            <span className={styles.statLabel}>Total Revenue</span>
          </div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statIcon}><FiDollarSign /></div>
          <div className={styles.statInfo}>
            <span className={styles.statValue}>{formatCurrency(totalProfit)}</span>
            <span className={styles.statLabel}>Total Profit</span>
          </div>
        </div>
      </div>

      {/* ── Filters ── */}
      <div className={styles.filtersBar}>
        <div className={styles.searchBox}>
          <FiSearch />
          <input
            type="text"
            placeholder="Search by customer name, phone, source, or notes..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className={styles.filterGroup}>
          <FiFilter />
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="all">All Status ({orders.length})</option>
            {statuses.map(s => (
              <option key={s.id} value={s.name}>
                {s.name} ({orders.filter(o => o.status === s.name).length})
              </option>
            ))}
          </select>
        </div>
        <div className={styles.batchActions}>
          <button
            onClick={generateBatchInvoice}
            className={styles.batchInvoiceBtn}
            disabled={selectedOrders.length === 0}
          >
            <FiPrinter /> Batch Invoice ({selectedOrders.length})
          </button>
        </div>
      </div>

      <div className={styles.resultsCount}>
        Showing {filteredOrders.length} of {orders.length} orders
      </div>

      {/* ── Orders Table ── */}
      {filteredOrders.length === 0 ? (
        <div className={styles.emptyState}>
          <div className={styles.emptyIcon}>📭</div>
          <h3>No orders found</h3>
          <p>Click "New Order" to add your first bulk order.</p>
        </div>
      ) : (
        <div className={styles.ordersTable}>
          <table>
            <thead>
              <tr>
                <th style={{ width: '30px' }}>
                  <input type="checkbox" checked={selectAll} onChange={toggleSelectAll} />
                </th>
                <th>Customer</th>
                <th>Phone</th>
                <th>Date</th>
                <th>Source</th>
                <th>Items</th>
                <th>Selling</th>
                <th>Cost</th>
                <th>Profit</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredOrders.map(order => {
                const margin = order.total_selling > 0
                  ? ((order.total_selling - order.total_cost) / order.total_selling * 100)
                  : 0;
                const belowTarget = margin < (order.profit_margin_target || 20);
                return (
                  <tr key={order.id}>
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedOrders.includes(order.id)}
                        onChange={() => toggleSelection(order.id)}
                      />
                    </td>
                    <td>
                      <span
                        className={styles.customerNameClickable}
                        onClick={() => openCustomerProfile(order.customer_name)}
                      >
                        {order.customer_name}
                      </span>
                      {order.notes && <small className={styles.orderNotes}>{order.notes.substring(0, 30)}</small>}
                    </td>
                    <td>{order.phone || 'N/A'}</td>
                    <td>{order.order_date ? new Date(order.order_date).toLocaleDateString() : 'N/A'}</td>
                    <td>
                      <span className={styles.sourceBadge}>{order.source || 'Other'}</span>
                      {order.assigned_to && (
                        <span className={styles.assignedBadge}>
                          👤 {admins.find(a => a.id === order.assigned_to)?.name || 'Unknown'}
                        </span>
                      )}
                    </td>
                    <td>{order.bulk_order_log_items?.length || 0}</td>
                    <td>{formatCurrency(order.total_selling)}</td>
                    <td>{formatCurrency(order.total_cost)}</td>
                    <td className={order.total_profit > 0 ? styles.profitPositive : order.total_profit < 0 ? styles.profitNegative : ''}>
                      {formatCurrency(order.total_profit)}
                      {belowTarget && order.total_selling > 0 && (
                        <span className={styles.marginWarning} title="Margin below target">
                          ⚠️ {margin.toFixed(0)}%
                        </span>
                      )}
                    </td>
                    <td>
                      <span
                        className={styles.statusBadge}
                        style={{ background: getStatusColor(order.status) + '20', color: getStatusColor(order.status) }}
                      >
                        {getStatusIcon(order.status)} {order.status?.toUpperCase()}
                      </span>
                    </td>
                    <td>
                      <div className={styles.actionButtons}>
                        <button onClick={() => openModal(order)} className={styles.editBtn}><FiEdit size={14} /></button>
                        <button onClick={() => duplicateOrder(order.id)} className={styles.duplicateBtn} title="Reorder"><FiCopy size={14} /></button>
                        <button onClick={() => deleteOrder(order.id)} className={styles.deleteBtn}><FiTrash2 size={14} /></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Add/Edit Modal ── */}
      {showModal && (
        <div className={styles.modalOverlay} onClick={closeModal}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>{editingId ? 'Edit Bulk Order' : 'New Bulk Order'}</h3>
              <button onClick={closeModal} className={styles.closeBtn}>×</button>
            </div>
            <div className={styles.modalBody}>
              {formError && <div className={styles.formError}>{formError}</div>}

              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label>Customer Name *</label>
                  <input
                    type="text"
                    name="customer_name"
                    value={formData.customer_name}
                    onChange={handleFormChange}
                    placeholder="Enter customer name"
                  />
                </div>
                <div className={styles.formGroup}>
                  <label>Phone Number</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleFormChange}
                    placeholder="Enter phone number"
                  />
                </div>
              </div>

              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label>Order Date</label>
                  <input
                    type="date"
                    name="order_date"
                    value={formData.order_date}
                    onChange={handleFormChange}
                  />
                </div>
                <div className={styles.formGroup}>
                  <label>Status</label>
                  <select name="status" value={formData.status} onChange={handleFormChange}>
                    {statuses.map(s => (
                      <option key={s.id} value={s.name}>{s.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label>Source</label>
                  <select name="source" value={formData.source} onChange={handleFormChange}>
                    <option value="WhatsApp">WhatsApp</option>
                    <option value="Instagram">Instagram</option>
                    <option value="Phone">Phone</option>
                    <option value="In-person">In-person</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div className={styles.formGroup}>
                  <label>Assigned To</label>
                  <select name="assigned_to" value={formData.assigned_to} onChange={handleFormChange}>
                    <option value="">Unassigned</option>
                    {admins.map(admin => (
                      <option key={admin.id} value={admin.id}>{admin.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label>GST Rate (%)</label>
                  <input
                    type="number"
                    name="gst_rate"
                    value={formData.gst_rate}
                    onChange={handleFormChange}
                    min="0"
                    max="100"
                    step="0.1"
                  />
                </div>
                <div className={styles.formGroup}>
                  <label>Discount (%)</label>
                  <input
                    type="number"
                    name="discount_percent"
                    value={formData.discount_percent}
                    onChange={handleFormChange}
                    min="0"
                    max="100"
                    step="0.1"
                  />
                </div>
              </div>

              <div className={styles.formGroup}>
                <label>Profit Margin Target (%)</label>
                <input
                  type="number"
                  name="profit_margin_target"
                  value={formData.profit_margin_target}
                  onChange={handleFormChange}
                  min="0"
                  max="100"
                  step="1"
                />
                <small>Used for smart pricing suggestions</small>
              </div>

              <div className={styles.formGroup}>
                <label>Notes</label>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleFormChange}
                  placeholder="Any extra details (e.g., Instagram handle)"
                  rows="2"
                />
              </div>

              {/* ── Line Items ── */}
              <div className={styles.itemsSection}>
                <div className={styles.itemsHeader}>
                  <h4>Products / Items</h4>
                  <button onClick={addItemRow} className={styles.addItemBtn}>
                    <FiPlus /> Add Item
                  </button>
                </div>

                <div className={styles.itemsTable}>
                  <table>
                    <thead>
                      <tr>
                        <th>Item Name</th>
                        <th style={{ width: '60px' }}>Qty</th>
                        <th style={{ width: '100px' }}>Cost Price</th>
                        <th style={{ width: '100px' }}>Selling Price</th>
                        <th style={{ width: '80px' }}>Profit</th>
                        <th style={{ width: '40px' }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((item, index) => {
                        const profit = (parseFloat(item.selling_price) || 0) - (parseFloat(item.cost_price) || 0);
                        const totalProfit = profit * (parseInt(item.quantity) || 0);
                        const margin = item.selling_price > 0
                          ? ((item.selling_price - item.cost_price) / item.selling_price * 100)
                          : 0;
                        const belowTarget = margin < (formData.profit_margin_target || 20);
                        return (
                          <tr key={index}>
                            <td>
                              <input
                                type="text"
                                value={item.item_name}
                                onChange={(e) => handleItemChange(index, 'item_name', e.target.value)}
                                placeholder="Product name"
                              />
                            </td>
                            <td>
                              <input
                                type="number"
                                min="1"
                                value={item.quantity}
                                onChange={(e) => handleItemChange(index, 'quantity', parseInt(e.target.value) || 1)}
                              />
                            </td>
                            <td>
                              <input
                                type="number"
                                min="0"
                                step="0.01"
                                value={item.cost_price}
                                onChange={(e) => handleItemChange(index, 'cost_price', parseFloat(e.target.value) || 0)}
                                className={styles.costInput}
                              />
                            </td>
                            <td>
                              <input
                                type="number"
                                min="0"
                                step="0.01"
                                value={item.selling_price}
                                onChange={(e) => handleItemChange(index, 'selling_price', parseFloat(e.target.value) || 0)}
                                className={styles.sellingInput}
                              />
                              {item.cost_price > 0 && !item.selling_price && (
                                <span className={styles.suggestHint}>
                                  Suggested: {formatCurrency(parseFloat(item.cost_price) * (1 + (formData.profit_margin_target || 20) / 100))}
                                </span>
                              )}
                            </td>
                            <td className={totalProfit > 0 ? styles.profitPositive : totalProfit < 0 ? styles.profitNegative : ''}>
                              {formatCurrency(totalProfit)}
                              {belowTarget && item.selling_price > 0 && (
                                <span className={styles.marginWarning} title="Margin below target">
                                  ⚠️ {margin.toFixed(0)}%
                                </span>
                              )}
                            </td>
                            <td>
                              <button
                                onClick={() => removeItemRow(index)}
                                className={styles.removeItemBtn}
                                disabled={items.length <= 1}
                              >
                                <FiX />
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* ── Totals ── */}
                <div className={styles.totalsSummary}>
                  <div className={styles.totalRow}>
                    <span>Total Selling:</span>
                    <strong>{formatCurrency(items.reduce((sum, i) => sum + (parseFloat(i.selling_price) || 0) * (parseInt(i.quantity) || 0), 0))}</strong>
                  </div>
                  <div className={styles.totalRow}>
                    <span>Total Cost:</span>
                    <strong>{formatCurrency(items.reduce((sum, i) => sum + (parseFloat(i.cost_price) || 0) * (parseInt(i.quantity) || 0), 0))}</strong>
                  </div>
                  <div className={styles.totalRow}>
                    <span>GST ({formData.gst_rate || 0}%):</span>
                    <strong>{formatCurrency((items.reduce((sum, i) => sum + (parseFloat(i.selling_price) || 0) * (parseInt(i.quantity) || 0), 0)) * (parseFloat(formData.gst_rate) || 0) / 100)}</strong>
                  </div>
                  <div className={styles.totalRow}>
                    <span>Discount:</span>
                    <strong>-{formatCurrency((items.reduce((sum, i) => sum + (parseFloat(i.selling_price) || 0) * (parseInt(i.quantity) || 0), 0)) * (parseFloat(formData.discount_percent) || 0) / 100)}</strong>
                  </div>
                  <div className={`${styles.totalRow} ${styles.totalProfitRow}`}>
                    <span>Total Profit:</span>
                    <strong className={
                      items.reduce((sum, i) => sum + ((parseFloat(i.selling_price) || 0) - (parseFloat(i.cost_price) || 0)) * (parseInt(i.quantity) || 0), 0) > 0
                        ? styles.profitPositive : styles.profitNegative
                    }>
                      {formatCurrency(items.reduce((sum, i) => sum + ((parseFloat(i.selling_price) || 0) - (parseFloat(i.cost_price) || 0)) * (parseInt(i.quantity) || 0), 0))}
                    </strong>
                  </div>
                  <div className={`${styles.totalRow} ${styles.grandTotalRow}`}>
                    <span>Grand Total (with tax & discount):</span>
                    <strong>
                      {formatCurrency(
                        items.reduce((sum, i) => sum + (parseFloat(i.selling_price) || 0) * (parseInt(i.quantity) || 0), 0)
                        + (items.reduce((sum, i) => sum + (parseFloat(i.selling_price) || 0) * (parseInt(i.quantity) || 0), 0)) * (parseFloat(formData.gst_rate) || 0) / 100
                        - (items.reduce((sum, i) => sum + (parseFloat(i.selling_price) || 0) * (parseInt(i.quantity) || 0), 0)) * (parseFloat(formData.discount_percent) || 0) / 100
                      )}
                    </strong>
                  </div>
                </div>
              </div>

              {/* ── Order Notes (Internal Wiki) ── */}
              {editingId && (
                <div className={styles.orderNotesSection}>
                  <h4>Internal Notes</h4>
                  <div className={styles.notesList}>
                    {orderNotes.map(note => (
                      <div key={note.id} className={styles.noteItem}>
                        <p>{note.note}</p>
                        <small>
                          by {note.users?.name || 'Admin'} on {new Date(note.created_at).toLocaleString()}
                        </small>
                      </div>
                    ))}
                  </div>
                  <div className={styles.addNoteRow}>
                    <input
                      type="text"
                      value={noteText}
                      onChange={(e) => setNoteText(e.target.value)}
                      placeholder="Add a note..."
                      onKeyDown={(e) => { if (e.key === 'Enter') addNote(); }}
                    />
                    <button onClick={addNote}><FiSend /></button>
                  </div>
                </div>
              )}
            </div>

            <div className={styles.modalFooter}>
              <button onClick={closeModal} className={styles.cancelBtn}>Cancel</button>
              <button onClick={handleSave} disabled={formLoading} className={styles.saveBtn}>
                {formLoading ? 'Saving...' : <><FiSave /> Save Order</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Customer Profile Modal ── */}
      {showCustomerProfile && customerProfile && (
        <div className={styles.modalOverlay} onClick={() => setShowCustomerProfile(false)}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3><FiUser /> {profileCustomer}</h3>
              <button onClick={() => setShowCustomerProfile(false)} className={styles.closeBtn}>×</button>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.profileStats}>
                <div className={styles.profileStat}>
                  <span className={styles.profileStatValue}>{customerProfile.total_orders || 0}</span>
                  <span className={styles.profileStatLabel}>Total Orders</span>
                </div>
                <div className={styles.profileStat}>
                  <span className={styles.profileStatValue}>{formatCurrency(customerProfile.total_spent || 0)}</span>
                  <span className={styles.profileStatLabel}>Total Spent</span>
                </div>
                <div className={styles.profileStat}>
                  <span className={styles.profileStatValue}>
                    {customerProfile.last_order_date ? new Date(customerProfile.last_order_date).toLocaleDateString() : 'N/A'}
                  </span>
                  <span className={styles.profileStatLabel}>Last Order</span>
                </div>
              </div>

              {/* Email / WhatsApp buttons */}
              <div className={styles.profileActions}>
                <button
                  onClick={() => window.open(`mailto:${customerProfile.email || ''}`)}
                  className={styles.profileEmailBtn}
                  disabled={!customerProfile.email}
                >
                  <FiMail /> Email
                </button>
                <button
                  onClick={() => window.open(`https://wa.me/91${customerProfile.phone || ''}`, '_blank')}
                  className={styles.profileWhatsappBtn}
                  disabled={!customerProfile.phone}
                >
                  <FiMessageSquare /> WhatsApp
                </button>
              </div>

              <div className={styles.profileOrders}>
                <h4>Order History</h4>
                {customerOrders.length === 0 ? (
                  <p>No orders found</p>
                ) : (
                  <table className={styles.profileOrderTable}>
                    <thead>
                      <tr><th>Date</th><th>Items</th><th>Total</th><th>Status</th></tr>
                    </thead>
                    <tbody>
                      {customerOrders.map(o => (
                        <tr key={o.id}>
                          <td>{new Date(o.created_at).toLocaleDateString()}</td>
                          <td>{o.bulk_order_log_items?.length || 0}</td>
                          <td>{formatCurrency(o.total_selling)}</td>
                          <td>
                            <span className={styles.statusBadge} style={{ background: getStatusColor(o.status) + '20', color: getStatusColor(o.status) }}>
                              {o.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Status Manager Modal ── */}
      {showStatusManager && (
        <div className={styles.modalOverlay} onClick={() => setShowStatusManager(false)}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>Manage Custom Statuses</h3>
              <button onClick={() => setShowStatusManager(false)} className={styles.closeBtn}>×</button>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.statusManager}>
                <div className={styles.addStatusRow}>
                  <input
                    type="text"
                    placeholder="New status name"
                    value={newStatusName}
                    onChange={(e) => setNewStatusName(e.target.value)}
                  />
                  <input
                    type="color"
                    value={newStatusColor}
                    onChange={(e) => setNewStatusColor(e.target.value)}
                    style={{ width: '50px', padding: '4px' }}
                  />
                  <button onClick={addCustomStatus} className={styles.addStatusBtn}>Add</button>
                </div>
                <div className={styles.statusList}>
                  {statuses.map(s => (
                    <div key={s.id} className={styles.statusItem}>
                      <span className={styles.statusDot} style={{ background: s.color }}></span>
                      <span>{s.name}</span>
                      {!s.is_default && (
                        <button onClick={() => deleteStatus(s.id)} className={styles.deleteStatusBtn}>✕</button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminBulkLog;